import { useEffect, useMemo, useState } from 'react';

import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import { ArtifactIcon } from '@/components/artifacts/components/ArtifactIcon';
// removed Card wrapper for minimal styling
import { ClearableInput } from '@/components/ui/ClearableInput';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetArtifactReferencesQuery } from '@/services/api/artifactDigestService';
import { useListArtifactsQuery } from '@/services/api/artifactService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

interface ArtifactBrowserPanelProps {
  matterId: string;
  requirementsById?: Record<string, unknown>;
  activeFieldKey?: string | null;
  /** Optional: render inside a subtle container when embedded beneath evidence */
  embedded?: boolean;
  /** Optional: initial global reference search term to filter artifacts */
  referenceSearchTerm?: string;
  /** Optional: callback when document is clicked instead of opening modal */
  onDocumentClick?: (artifactId: string) => void;
}

/**
 * Compact, opinionated browser for artifacts related to claim data requirements.
 * - Tabs: Related (from other requirements), All documents
 * - Single compact list, no filters
 * - Clean rows: icon + clickable title that opens the inspector modal
 */
export const ArtifactBrowserPanel = ({
  matterId,
  requirementsById,
  activeFieldKey,
  embedded = false,
  referenceSearchTerm,
  onDocumentClick,
}: ArtifactBrowserPanelProps) => {
  const {
    data: allArtifacts = [],
    isLoading,
    isError,
  } = useListArtifactsQuery({ matterId }, { skip: !matterId });

  // Build map: artifactId -> { citationIds, requirementKeys }
  const relatedByArtifactId = useMemo(() => {
    const map = new Map<
      string,
      { citationIds: string[]; requirementKeys: string[] }
    >();
    if (!requirementsById) return map;
    Object.entries(requirementsById).forEach(([reqKey, reqVal]) => {
      if (reqKey === activeFieldKey) return;
      const values =
        (reqVal as unknown as { values?: unknown[] })?.values || [];
      values.forEach((v) => {
        const ctx = (v as unknown as { context?: Record<string, unknown> })
          ?.context;
        const id = (ctx?.artifact_id as string | undefined) || undefined;
        const cits = (ctx?.citation_ids as string[] | undefined) || [];
        if (!id) return;
        const prev = map.get(id) || { citationIds: [], requirementKeys: [] };
        const mergedCitations = Array.from(
          new Set([...prev.citationIds, ...cits]),
        );
        const mergedReqs = Array.from(
          new Set([...prev.requirementKeys, reqKey]),
        );
        map.set(id, {
          citationIds: mergedCitations,
          requirementKeys: mergedReqs,
        });
      });
    });
    return map;
  }, [requirementsById, activeFieldKey]);

  const relatedArtifacts: ArtifactUnpacked[] = useMemo(
    () =>
      allArtifacts
        .filter((a) => relatedByArtifactId.has(a.id))
        .sort((a, b) => {
          const ra = relatedByArtifactId.get(a.id)?.requirementKeys.length || 0;
          const rb = relatedByArtifactId.get(b.id)?.requirementKeys.length || 0;
          if (rb !== ra) return rb - ra;
          return (
            new Date(b.modified_at).getTime() -
            new Date(a.modified_at).getTime()
          );
        }),
    [allArtifacts, relatedByArtifactId],
  );

  const allArtifactsSorted: ArtifactUnpacked[] = useMemo(
    () =>
      allArtifacts
        .slice()
        .sort(
          (a, b) =>
            new Date(b.modified_at).getTime() -
            new Date(a.modified_at).getTime(),
        ),
    [allArtifacts],
  );

  const hasRelated = relatedArtifacts.length > 0;
  const [tab, setTab] = useState<'related' | 'all'>('all');
  useEffect(() => {
    if (!hasRelated && tab !== 'all') setTab('all');
  }, [hasRelated, tab]);

  const rows = tab === 'related' ? relatedArtifacts : allArtifactsSorted;

  // Global reference search state (defaults to prop if provided)
  const [searchTerm, setSearchTerm] = useState<string>(
    referenceSearchTerm || '',
  );
  useEffect(() => {
    setSearchTerm(referenceSearchTerm || '');
  }, [referenceSearchTerm]);

  // Derived trimmed term for conditional rendering
  const term = (searchTerm || '').trim();

  // Track how many rows were evaluated and how many matched during search
  const [evaluatedCount, setEvaluatedCount] = useState(0);
  const [matchedCount, setMatchedCount] = useState(0);
  useEffect(() => {
    setEvaluatedCount(0);
    setMatchedCount(0);
  }, [rows, term]);

  const containerClass = embedded ? '' : '';

  return (
    <div className={containerClass} aria-label="Document browser">
      <div className="">
        <div className="py-2">
          <div className="flex items-center justify-between gap-3">
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as 'related' | 'all')}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="All documents">
                  All
                  <span className="ml-1 text-[10px] text-gray-600">
                    ({allArtifactsSorted.length})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="related" aria-label="Related documents">
                  Related
                  <span className="ml-1 text-[10px] text-gray-600">
                    ({relatedArtifacts.length})
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="w-96 md:w-[32rem]">
              <ClearableInput
                placeholder="Search references across documents…"
                value={searchTerm}
                onChange={setSearchTerm}
                className="h-9 text-sm shadow-none ring-0 focus:ring-0 focus-visible:ring-0 focus:outline-none"
                aria-label="Search references"
              />
            </div>
          </div>
        </div>
        <div className="pt-0">
          {isLoading && (
            <div className="text-sm text-gray-600 p-3">Loading documents…</div>
          )}
          {isError && (
            <div className="text-sm text-red-600 p-3">
              Unable to load documents
            </div>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <div className="text-sm text-gray-600 p-3">
              No documents available
            </div>
          )}

          {!isLoading && !isError && rows.length > 0 && (
            <ul className="divide-y">
              {rows.map((artifact) => {
                const displayName = (() => {
                  // 1) Blob: prefer uploaded file name if present
                  if (artifact.artifact_type === ArtifactType.BLOB) {
                    const blobName =
                      (artifact as { blob?: { name?: string | null } }).blob
                        ?.name || null;
                    if (blobName) return blobName;
                  }

                  // 2) Prefer digest title
                  const title = artifact.artifact_digest?.title || null;
                  if (title) return title;

                  // 3) Then one-line summary
                  const summary =
                    artifact.artifact_digest?.one_line_summary || null;
                  if (summary) return summary;

                  // 4) Rich fallback by type with modified date
                  const modified = new Date(artifact.modified_at);
                  const dateStr = Number.isNaN(modified.getTime())
                    ? ''
                    : ` • ${modified.toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                      })}`;

                  switch (artifact.artifact_type) {
                    case ArtifactType.TRANSCRIPT:
                      return `Transcript${dateStr}`;
                    case ArtifactType.AUDIO_CONVERSATION:
                      return `Audio Conversation${dateStr}`;
                    case ArtifactType.CONVERSATION:
                      return `Conversation${dateStr}`;
                    case ArtifactType.STAFF_NOTE:
                      return `Staff Note${dateStr}`;
                    case ArtifactType.BLOB:
                    default:
                      return `Document${dateStr}`;
                  }
                })();

                // Click-to-open inspector modal
                const fileMime =
                  (
                    artifact as unknown as {
                      blob?: { file_mime_type?: string };
                    }
                  )?.blob?.file_mime_type || '';
                const isImageBlob =
                  artifact.artifact_type === ArtifactType.BLOB &&
                  fileMime.startsWith('image/');

                return (
                  <FilteredArtifactRow
                    key={artifact.id}
                    id={artifact.id}
                    title={displayName}
                    type={artifact.artifact_type}
                    isImage={isImageBlob}
                    referenceSearchTerm={searchTerm}
                    onEvaluated={() => setEvaluatedCount((c) => c + 1)}
                    onMatched={() => setMatchedCount((c) => c + 1)}
                    onDocumentClick={onDocumentClick}
                  />
                );
              })}
            </ul>
          )}

          {/* When searching and every row has been evaluated with no matches, show a friendly empty state */}
          {!isLoading &&
            !isError &&
            rows.length > 0 &&
            term.length > 0 &&
            evaluatedCount === rows.length &&
            matchedCount === 0 && (
              <div className="py-10 flex items-center justify-center">
                <div className="text-sm text-gray-600">
                  No results found for{' '}
                  <span className="font-medium">“{term}”</span>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

interface BaseRowProps {
  id: string;
  title: string;
  type: ArtifactType;
  isImage?: boolean;
}

interface FilteredArtifactRowProps extends BaseRowProps {
  referenceSearchTerm?: string;
  onEvaluated?: () => void;
  onMatched?: () => void;
  onDocumentClick?: (artifactId: string) => void;
}

const FilteredArtifactRow = ({
  id,
  title,
  type,
  isImage,
  referenceSearchTerm,
  onEvaluated,
  onMatched,
  onDocumentClick,
}: FilteredArtifactRowProps) => {
  // Hooks must be first
  const [isOpen, setIsOpen] = useState(false);
  const [hasReportedForTerm, setHasReportedForTerm] = useState(false);

  const renderHighlighted = (text: string | null | undefined) => {
    const value = text || '';
    const t = (referenceSearchTerm || '').trim();
    if (!t) return value;
    const idx = value.toLowerCase().indexOf(t.toLowerCase());
    if (idx === -1) return value;
    const before = value.slice(0, idx);
    const match = value.slice(idx, idx + t.length);
    const after = value.slice(idx + t.length);
    return (
      <span className="truncate" title={value}>
        {before}
        <mark className="bg-yellow-100 text-gray-900 px-0.5 rounded">
          {match}
        </mark>
        {after}
      </span>
    );
  };

  // If no search term provided, render the simple row
  const term = (referenceSearchTerm || '').trim().toLowerCase();
  const { data: references = [], isLoading } = useGetArtifactReferencesQuery(
    id,
    {
      skip: term.length === 0,
    },
  );

  // Reset reporting state when the term (or artifact) changes
  useEffect(() => {
    setHasReportedForTerm(false);
  }, [term, id]);

  // Compute matches (safe even while loading; effect below guards isLoading)
  const matched = references.filter((ref) => {
    const v = `${ref.value || ''}`.toLowerCase();
    const d = `${ref.description || ''}`.toLowerCase();
    const t = `${ref.type || ''}`.toLowerCase();
    return v.includes(term) || d.includes(term) || t.includes(term);
  });

  // Inform parent about evaluation/match once per artifact per term
  useEffect(() => {
    if (term.length === 0 || isLoading || hasReportedForTerm) return;
    onEvaluated?.();
    if (matched.length > 0) onMatched?.();
    setHasReportedForTerm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, isLoading, matched.length, hasReportedForTerm]);

  const handleClick = () => {
    if (onDocumentClick) {
      onDocumentClick(id);
    } else {
      setIsOpen(true);
    }
  };

  const header = (
    <div className="flex items-center gap-3">
      <div className="shrink-0">
        <ArtifactIcon type={type} isImage={isImage} />
      </div>
      <button
        className="min-w-0 text-left truncate text-sm font-medium text-indigo-600 hover:underline"
        type="button"
        onClick={handleClick}
        aria-label={`Open ${title}`}
      >
        <span className="truncate" title={title}>
          {title}
        </span>
      </button>
    </div>
  );

  if (term.length === 0) {
    return (
      <li className="px-3 py-2 hover:bg-gray-50">
        {header}
        {!onDocumentClick && (
          <InspectArtifactModal
            artifactId={id}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
          />
        )}
      </li>
    );
  }

  // When searching, include an artifact only if any reference matches
  if (isLoading) {
    return null; // avoid flicker while searching; list stabilizes after load
  }

  if (matched.length === 0) return null;

  return (
    <li className="px-3 py-2 hover:bg-gray-50">
      {header}

      <div className="ml-8 mt-1 space-y-1">
        {matched.slice(0, 3).map((m, i) => (
          <div
            key={i}
            className="flex items-center gap-2 text-xs text-gray-600"
          >
            <div className="min-w-0 truncate">
              {renderHighlighted(m.value ?? m.description ?? '')}
            </div>
            {(m.type || m.description) && (
              <span className="bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 text-[10px] whitespace-nowrap">
                {m.type || m.description}
              </span>
            )}
          </div>
        ))}
      </div>

      {!onDocumentClick && (
        <InspectArtifactModal
          artifactId={id}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </li>
  );
};
